import pegasus from '../../assets/scripts/global/01-pegasus';

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 * 	Usage:
 * 	Add link with the attr 'data-pegasus-modal'.
 * 	Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 * 	To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 * 	Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {
	return {
		init() {
			const _this = this;
			const modalSkeleton = pegasus.stringToHTML(document.getElementById('Modal__Skeleton').innerHTML);
			// Filter out links that are already initialized to allow safe re-initialization
			const allLinks = document.querySelectorAll('[data-pegasus-modal]');
			const uninitializedLinks = Array.from(allLinks).filter((link) => !link.hasAttribute('data-pegasus-modal-id'));
			const modalLinks = _this.createModalLinks(uninitializedLinks);

			modalLinks.forEach((link, index) => {
				const modal = modalSkeleton.cloneNode(true);

				let cssClass = 'Modal',
					modalNav;

				//check for classes to add
				if (link.dataset && link.dataset.pegasusModal && link.dataset.pegasusModal.length) {
					cssClass += ' ' + link.dataset.pegasusModal;

					//if nav specified
					if (link.dataset.pegasusModal.includes('Modal--with-nav')) {
						modalNav = _this.buildModalNav(index, modalLinks);

						if (modalNav) {
							modal.querySelector('.Modal__navigation').append(modalNav);
						}
					}
				}

				//setup classes and id
				modal.className = cssClass;
				modal.id = link.dataset.pegasusModalId;

				//append modal template to page if it does nt already exist
				if (link.dataset && !document.getElementById(link.dataset.pegasusModalId)) {
					document.body.append(modal);
				}

				// Pre-fetch ≤4 modals when the browser is idle so the request
				// doesn't appear in the critical chain.
				if (modalLinks.length <= 4) {
					const prefetch = () => _this.loadModalContent(link.href, link.dataset.pegasusModalId);
					if ('requestIdleCallback' in window) {
						requestIdleCallback(prefetch, { timeout: 3000 });
					} else {
						setTimeout(prefetch, 2000);
					}
				}
			});

			// ── Deferred handler state ──────────────────────────────
			// Tracks per-modal state so dynamically imported content
			// handlers (e.g. HubSpot, CF7) can defer their heavy work
			// until the modal is actually opened — even when content
			// is pre-fetched.
			this._modalState = {};

			window.addEventListener('modalopen', (evt) => {
				const id = evt.detail?.modalID;
				if (!id) return;

				const state = (this._modalState[id] = this._modalState[id] || {
					modalBody: null,
					handlerImports: [],
					opened: false,
				});
				state.opened = true;

				// Content already fetched — fire deferred handlers now.
				if (state.modalBody) {
					this._runModalOpenHandlers(id);
				}
			});

			window.modal = this;
		},

		doesLinkAlreadyExist(link, linksArray) {
			return linksArray.find((element) => element.href === link.href);
		},

		createModalLinks(links) {
			const _this = this;
			const modalLinks = [];

			if (links.length) {
				links.forEach((link, index) => {
					const container = link;

					if (!link || link.tagName.toLowerCase() !== 'a') {
						const innerAnchor = container.querySelector('a[href]');
						if (!innerAnchor) return; // No actionable link

						// Ensure modal data attributes exist on the actionable anchor
						if (!innerAnchor.dataset.pegasusModal && container.dataset.pegasusModal) {
							innerAnchor.setAttribute('data-pegasus-modal', container.dataset.pegasusModal);
						}
						if (!innerAnchor.dataset.pegasusModalId && container.dataset.pegasusModalId) {
							innerAnchor.setAttribute('data-pegasus-modal-id', container.dataset.pegasusModalId);
						}

						link = innerAnchor;
					}

					// Validate the href — skip empty, fragment-only, or javascript: links.
					// We intentionally do NOT append ?modal=yes to the actual href so that
					// right-click → "Open in new tab" loads the full page.
					// The parameter is added at fetch-time in loadModalContent() instead.
					const rawHref = link.getAttribute('href') || '';

					if (rawHref === '' || rawHref.startsWith('#') || rawHref.toLowerCase().startsWith('javascript:')) {
						return;
					}

					try {
						new URL(rawHref, window.location.href);
					} catch (e) {
						return;
					}

					const existingLink = _this.doesLinkAlreadyExist(link, modalLinks);

					//check for existing link and copy id if true
					if (existingLink) {
						link.setAttribute('data-pegasus-modal-id', existingLink.dataset.pegasusModalId);
					} else {
						link.setAttribute('data-pegasus-modal-id', `modal-${index}`);
					}

					//if there are 5 or more modals, add 'click' to load more
					if (links.length > 4) {
						link.addEventListener('click', (e) => {
							e.preventDefault();
							_this.loadModalContent(link.href, link.dataset.pegasusModalId);
						});
					}

					//add event to emit 'modalopen' event for specified modal
					link.addEventListener('click', _this.emitModalOpen);

					//check we're not adding existing links.
					if (!existingLink) modalLinks.push(link);
				});
			}

			return modalLinks;
		},

		emitModalOpen(e) {
			e.preventDefault();

			const modalID = e.currentTarget.dataset.pegasusModalId;
			const myEvent = new CustomEvent('modalopen', {
				detail: { modalID },
			});

			window.dispatchEvent(myEvent);
		},

		buildModalNav(currentIndex, allLinksArray) {
			const _this = this;

			let prevLinkHTML, nextLinkHTML;

			//check if we're at the start or end
			let prevLink = currentIndex > 0 ? allLinksArray[currentIndex - 1] : '';
			let nextLink = currentIndex < allLinksArray.length - 1 ? allLinksArray[currentIndex + 1] : '';

			//check if links are modals with navs
			if (prevLink !== '') prevLink = prevLink.dataset.pegasusModal.includes('Modal--with-nav') ? prevLink : '';
			if (nextLink !== '') nextLink = nextLink.dataset.pegasusModal.includes('Modal--with-nav') ? nextLink : '';

			if (prevLink !== '') {
				const target = prevLink.dataset.pegasusModalId;
				const prevHref = prevLink.getAttribute('href') || '';

				prevLinkHTML = prevHref
					? `<a href="${prevHref}" class="prev-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="prev-link arrow-btn -disabled"></span>`;
			} else {
				prevLinkHTML = `<span class="prev-link arrow-btn -disabled"></span>`;
			}

			if (nextLink !== '') {
				const target = nextLink.dataset.pegasusModalId;
				const nextHref = nextLink.getAttribute('href') || '';

				nextLinkHTML = nextHref
					? `<a href="${nextHref}" class="next-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="next-link arrow-btn -disabled"></span>`;
			} else {
				nextLinkHTML = `<span class="next-link arrow-btn -disabled"></span>`;
			}

			//create modalNav DOM element
			const modalNav = pegasus.stringToHTML(`<div class="links">${prevLinkHTML} ${nextLinkHTML}</div>`);

			//set up modal open and ajax events on modal nav links
			modalNav.querySelectorAll('[data-pegasus-modal-id]').forEach((link) => {
				link.addEventListener('click', _this.emitModalOpen);

				link.addEventListener('click', (e) => {
					e.preventDefault();
					_this.loadModalContent(link.href, link.dataset.pegasusModalId);
				});
			});

			return modalNav;
		},

		loadModalContent(href, modalID) {
			let url;
			try {
				url = new URL(href, window.location.href);
			} catch (e) {
				return;
			}
			// Append ?modal=yes at fetch-time only — the actual <a href> stays
			// clean so right-click → "Open in new tab" loads the full page. OD
			url.searchParams.set('modal', 'yes');

			let lastModalBody = null;

			pegasus
				.fetchContent(url)
				.then((resp) => {
					if (!resp.ok) throw Error(resp.statusText);

					return resp.text();
				})
				.then((htmlResponse) => {
					const modalHTML = htmlResponse;

					const modalBody = document.querySelector(`#${modalID} .Modal__body`);

					modalBody.innerHTML = modalHTML;

					lastModalBody = modalBody;

					return modalBody;
				})
				.then((modalBody) => {
					//reinit anything else here...
					pegasus.decodeAllCloudflareEmails(modalBody);
				})
				.catch((error) => {
					const modalBody = document.querySelector(`#${modalID} .Modal__body`),
						errorContainer = modalBody.querySelector('.Modal__error');

					if (typeof errorContainer === 'undefined' || errorContainer === null) return;

					errorContainer.classList.remove('hidden');
				})
				.finally(() => {
					if (document.lazyLoadInstance) {
						document.lazyLoadInstance.update();
					}

					// Detect which content handlers are needed and queue
					// their dynamic imports. The actual handler modules are
					// only fetched by the browser when content matches —
					// keeping the main bundle lean.
					if (lastModalBody) {
						const state = (this._modalState[modalID] = this._modalState[modalID] || {
							modalBody: null,
							handlerImports: [],
							opened: false,
						});
						state.modalBody = lastModalBody;

						state.handlerImports = this._detectHandlers(lastModalBody);

						if (state.opened) {
							this._runModalOpenHandlers(modalID);
						}
					}
				});
		},

		/**
		 * Lightweight detection — checks the DOM for known form
		 * patterns and returns an array of dynamic import() thunks
		 * for only the handlers that are needed.
		 *
		 * No handler code is loaded until one of these thunks is called.
		 */
		_detectHandlers(modalBody) {
			const imports = [];

			// HubSpot: hs-form-frame embeds OR legacy inline scripts
			const hasHsFrame = modalBody.querySelector('.hs-form-frame[data-form-id]');
			const hasHsScript = (() => {
				for (const s of modalBody.querySelectorAll('script')) {
					if (s.src && s.src.includes('hsforms')) return true;
					if ((s.textContent || '').includes('hbspt.forms.create')) return true;
				}
				return false;
			})();

			if (hasHsFrame || hasHsScript) {
				imports.push(() => import('./handlers/hubspot.js'));
			}

			// Contact Form 7: .wpcf7 wrapper + global wpcf7 object
			if (typeof wpcf7 !== 'undefined' && wpcf7 !== null && modalBody.querySelector('.wpcf7 > form')) {
				imports.push(() => import('./handlers/cf7.js'));
			}

			return imports;
		},

		/**
		 * Dynamically import and execute every detected handler for a
		 * given modal. Called once per modal when both conditions are
		 * met: content is loaded AND modal is opened.
		 */
		_runModalOpenHandlers(modalID) {
			const state = this._modalState[modalID];
			if (!state || !state.modalBody || !state.handlerImports.length) return;

			state.handlerImports.forEach((importFn) => {
				importFn()
					.then((mod) => {
						if (typeof mod.onModalOpen === 'function') {
							mod.onModalOpen(state.modalBody, modalID);
						}
					})
					.catch(() => {});
			});

			// Clean up so handlers don't fire twice on the same modal.
			state.handlerImports = [];
		},
	};
};

/**
 * singleModal
 *
 * Component code for a single modal
 *
 * @since  2.0.0
 */
window.singleModal = () => {
	return {
		open: false,

		handleModalOpen(evt) {
			//only run on actual modals, not skeleton
			if (this.$el.classList.contains('ModalSkeleton')) return;

			document.body.classList.add('modal-open');
			document.body.style.overflow = 'hidden';
			this.open = this.$el.id === evt.detail.modalID;
		},

		handleModalClose() {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
			this.open = false;
		},
	};
};
